<template>
  <div
    :class="[
      'owc-media-slider',
      { 'owc-media-slider__dark': isDarkMode },
      { 'owc-media-slider__isEditMode': isEditMode },
    ]"
  >
    <ValidationBoxWrapper>
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <template v-if="isEditMode">
      <SlotContainer
        class="owc-media-slider__slotContainer"
        refName="slot-media-slider"
        :slot-component-data="JSON.stringify({ darkMode: isDarkMode })"
      >
        <slot></slot>
      </SlotContainer>
    </template>
    <wb-slider
      v-else
      off-grid
      grid-gutter
      show-dot-navigation
      :show-arrow-navigation="!isMobile"
      scroll-snap
      ref="mediaSliderRef"
      class="owc-media-slider__slider"
      @wbchange="sliderChanged"
      :theme="isDarkMode ? 'dark' : 'light'"
    >
      <SlotContainer
        v-for="(sliderItem, index) in sliderItems"
        :key="index"
        class="wb-slider__item wb-grid-col-mq1-11 wb-grid-col-mq3-6"
        refName="slot-media-slider"
        :slotName="sliderItem.id"
        :slot-component-data="JSON.stringify({ isActive: itemIsActive(index), darkMode: isDarkMode })"
      >
        <slot :slot="sliderItem.id" :name="sliderItem.id" />
      </SlotContainer>
    </wb-slider>
    <div v-if="footnotes" class="wb-grid-container owc-media-slider__footnotes">
      <globalelem-footnote-component
        :module-id="moduleId"
        :theme="isDarkMode ? 'dark' : 'light'"
      ></globalelem-footnote-component>
    </div>
  </div>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { MediaSliderComponent, Payload } from './MediaSlider';
import MediaSliderItem from '@owc-packages/MediaSlider/src/MediaSliderItem.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src/ValidationBoxWrapper.vue';
import { mobileBreakpoints } from '@owc-utils/MediaQueryService';
import { getComponentStateConnection } from '@owc-utils/Store/owcComponent';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';

export default (ComponentInitializeDispatcher as MediaSliderComponent).extend({
  name: 'owc-media-slider',
  mixins: [OwcDataComponent],
  components: {
    MediaSliderItem,
    SlotContainer,
    ValidationBoxWrapper,
  },
  data: () => ({
    indexSlider: 0,
    isMobile: false,
    mediaSliderItemsIds: [],
  }),
  created() {
    this.isOnMobileView();
    this.onMQChange?.(() => {
      this.isOnMobileView();
      this.calculateActiveSlides();
    });
  },
  mounted() {
    this.generateMediaSliderItemsIdsArray();
  },
  computed: {
    sliderItems() {
      return this.componentData?.items;
    },
    isDarkMode: function() {
      return Boolean(this.componentData?.darkMode);
    },
    footnotes() {
      return getLocalFootnotes(this.componentData);
    },
    moduleId() {
      return setModuleId(this);
    },
  },
  methods: {
    sliderChanged(event) {
      const videoPlayerItems = Array.from(
        this.getHost().querySelectorAll('owc-media-slider owc-video-player'),
      );
      this.indexSlider = event.detail - 1;
      videoPlayerItems.forEach(videoPlayer => {
        const playButton = videoPlayer.shadowRoot?.querySelector(
          '.plyr--playing button[data-plyr="play"]',
        ) as HTMLButtonElement;
        if (playButton) {
          playButton.click();
        }
      });
      this.calculateActiveSlides();
    },
    async calculateActiveSlides() {
      const connection = await getComponentStateConnection();
      const payload: Payload[] = this.mediaSliderItemsIds.map((item, index) => ({
        id: item,
        json: {
          isActive: this.itemIsActive(index),
        },
      }));
      connection.updateComponents(payload);
    },
    isOnMobileView() {
      const currentMq = this.getCurrentMediaQuery?.();
      this.isMobile = Boolean(currentMq && mobileBreakpoints.includes(currentMq));
    },
    itemIsActive(index) {
      return index === this.indexSlider || (!this.isMobile && index === this.indexSlider + 1);
    },
    generateMediaSliderItemsIdsArray() {
      const host = this.getHost();

      if (host) {
        const mediaSliderItems = Array.from(host.querySelectorAll('owc-media-slider-item'));
        this.mediaSliderItemsIds = mediaSliderItems
          .map(item => item.getAttribute('component-id') as string)
          .filter(item => item);
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rte';

.owc-media-slider {
  padding-bottom: var(--wb-spacing-s);

  &__dark {
    background: var(--wb-black);
  }
  wb-slider wb-dot-navigation {
    margin-top: var(--wb-spacing-m);
  }
  .owc-media-slider__footnotes {
    padding-top: var(--wb-spacing-s);
  }
}
</style>

<style lang="scss">
.owc-media-slider__isEditMode {
  @include wb-content-width;

  .owc-media-slider__slotContainer {
    ::slotted(.webcomponent-nested) {
      border: dashed 2px var(--wb-grey-70);
    }
  }
}
</style>
